import React, { forwardRef } from "react";
import { Link, NavLink } from "react-router-dom";
import "./Button.scss";

// Fontaine M11 — the one Button. Visual contract: Style Guide V2 `button-*` specimens.
const BUTTON_SIZES = ["sm", "md", "lg", "xl"];
const BUTTON_VARIANTS = ["primary", "secondary", "ghost", "danger"];
// Kept so existing call sites keep rendering until Phase 3 migrates them. Registry marks them legacy.
const LEGACY_VARIANTS = { outline: "secondary", icon: "ghost", success: "success", tab: "tab" };
const LEGACY_SIZES = { "2xl": "xl" };

/**
 * @param {"primary"|"secondary"|"ghost"|"danger"} [variant="primary"]
 * @param {"sm"|"md"|"lg"|"xl"} [size="md"]           md = 32px, lg = 40px (page-header CTA), 44px touch target on phones
 * @param {boolean} [fullWidth]                        100% width (stacks in phone action rows)
 * @param {boolean} [loading]                          shows the spinner, disables interaction, sets aria-busy
 * @param {ReactNode} [leadingIcon] / [trailingIcon]   usually <Icon name=… />
 * @param {"sm"|"md"} [hideLabelBelow]                 icon-only below 640 / 900px; label stays for assistive tech
 * @param {boolean} [hero]                             page-header CTA: pins a shared min-width so pairs match
 * @param {"center"|"start"} [align="center"]         start = label at the start edge, trailing icon at the end (full-width disclosure rows)
 * @param {string} [to] / [href]                       renders <Link> / <a>; `nav` renders <NavLink>
 */
const Button = forwardRef(function Button(
  {
    children,
    size = "md",
    variant = "primary",
    fullWidth = false,
    block = false, // legacy alias of fullWidth
    loading = false,
    leadingIcon = null,
    trailingIcon = null,
    hideLabelBelow,
    hero = false,
    align = "center",
    to,
    href,
    exact = false,
    nav = false,
    active = false,
    disabled = false,
    type = "button",
    className = "",
    onClick,
    title,
    ...rest
  },
  ref
) {
  const resolvedSize = BUTTON_SIZES.includes(size) ? size : LEGACY_SIZES[size] || "md";
  const resolvedVariant = BUTTON_VARIANTS.includes(variant) ? variant : LEGACY_VARIANTS[variant] || "primary";
  // Legacy hero class from existing call sites → the modifier.
  const isHero = hero || /\brc-ui-btn--hero\b/.test(className);
  const extra = className.replace(/\brc-ui-btn--hero\b/, "").trim();
  const inert = disabled || loading;

  const classes = [
    "ft-btn",
    `ft-btn--${resolvedSize}`,
    `ft-btn--${resolvedVariant}`,
    fullWidth || block ? "ft-btn--full" : "",
    isHero ? "ft-btn--hero" : "",
    align === "start" ? "ft-btn--align-start" : "",
    active ? "ft-btn--active" : "",
    disabled ? "ft-btn--disabled" : "",
    loading ? "ft-btn--loading" : "",
    hideLabelBelow ? `ft-btn--hide-label-${hideLabelBelow}` : "",
    extra,
  ]
    .filter(Boolean)
    .join(" ");

  // `to`/`href` render anchors, which ignore `disabled` — swallow the click.
  const handleClick = inert ? (e) => e.preventDefault() : onClick;

  // A disabled control cannot show its own title (pointer-events: none + browser behaviour),
  // so it is hoisted onto a hoverable wrapper — "disabled + explain why" stays possible.
  const withTooltip = (el) =>
    disabled && title ? (
      <span className="ft-tooltip-anchor" title={title}>
        {el}
      </span>
    ) : (
      el
    );
  const ownTitle = disabled && title ? undefined : title;

  const content = (
    <>
      {leadingIcon ? <span className="ft-btn__icon" aria-hidden="true">{leadingIcon}</span> : null}
      {children != null && children !== false ? <span className="ft-btn__label">{children}</span> : null}
      {trailingIcon ? <span className="ft-btn__icon" aria-hidden="true">{trailingIcon}</span> : null}
    </>
  );

  const shared = {
    ref,
    className: classes,
    title: ownTitle,
    onClick: handleClick,
    "aria-busy": loading || undefined,
    ...rest,
  };
  const anchorA11y = { "aria-disabled": inert || undefined, tabIndex: inert ? -1 : undefined };

  if (to) {
    if (nav || variant === "tab") {
      return withTooltip(
        <NavLink to={to} exact={exact} activeClassName="ft-btn--active" {...anchorA11y} {...shared}>
          {content}
        </NavLink>
      );
    }
    return withTooltip(
      <Link to={to} {...anchorA11y} {...shared}>
        {content}
      </Link>
    );
  }

  if (href) {
    return withTooltip(
      <a href={inert ? undefined : href} {...anchorA11y} {...shared}>
        {content}
      </a>
    );
  }

  return withTooltip(
    <button type={type} disabled={disabled} aria-disabled={loading || undefined} {...shared}>
      {content}
    </button>
  );
});

export default Button;
